/**
 * Canva HTML Parser
 *
 * Parses Canva email HTML exports to extract text blocks and buttons.
 *
 * Canva HTML structure:
 * - Text blocks are in `<td dir="ltr" ...>` elements
 * - Buttons are `<a href="..." style="...background...">` with nested `<span>`
 * - HTML often has duplicate structures for responsive layouts (desktop/mobile)
 */

export interface TextBlock {
  id: string;
  content: string;
  preview: string;
  selector: string;
  style: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
  };
}

export interface Button {
  id: string;
  text: string;
  originalHref: string;
  selector: string;
  style: {
    backgroundColor?: string;
    textColor?: string;
  };
}

export interface ParsedCanvaTemplate {
  textBlocks: TextBlock[];
  buttons: Button[];
  originalHtml: string;
}

/**
 * Parse Canva HTML to extract text blocks and buttons
 */
export function parseCanvaHtml(html: string): ParsedCanvaTemplate {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const textBlocks = extractTextBlocks(doc);
  const buttons = extractButtons(doc);

  return {
    textBlocks,
    buttons,
    originalHtml: html
  };
}

/**
 * Extract text blocks from Canva HTML
 *
 * Look for `<td dir="ltr">` elements with actual text content.
 * Deduplicate based on text content (responsive layouts have duplicates).
 */
function extractTextBlocks(doc: Document): TextBlock[] {
  const blocks: TextBlock[] = [];
  const seenContent = new Set<string>();

  // Find all td elements with dir="ltr" - this is Canva's text container pattern
  const tdElements = doc.querySelectorAll('td[dir="ltr"]');

  let blockId = 0;
  tdElements.forEach((td, index) => {
    // Get text content, clean it up
    const rawText = td.textContent || '';
    const cleanedText = cleanTextContent(rawText);

    // Skip empty or whitespace-only content
    if (!cleanedText.trim()) return;

    // Skip if we've already seen this exact content (responsive duplicates)
    if (seenContent.has(cleanedText)) return;
    seenContent.add(cleanedText);

    // Extract style information
    const style = td.getAttribute('style') || '';
    const fontSize = extractStyleValue(style, 'font-size');
    const fontWeight = extractStyleValue(style, 'font-weight');
    const color = extractStyleValue(style, 'color');

    blocks.push({
      id: `text_${++blockId}`,
      content: cleanedText,
      preview: cleanedText.length > 50 ? cleanedText.substring(0, 50) + '...' : cleanedText,
      selector: `td[dir="ltr"]:nth-of-type(${index + 1})`,
      style: {
        fontSize: fontSize || undefined,
        fontWeight: fontWeight || undefined,
        color: color || undefined
      }
    });
  });

  return blocks;
}

/**
 * Extract buttons from Canva HTML
 *
 * Look for `<a>` elements with background styling (indicates a button).
 * Deduplicate based on button text.
 */
function extractButtons(doc: Document): Button[] {
  const buttons: Button[] = [];
  const seenText = new Set<string>();

  // Find all anchor elements
  const anchorElements = doc.querySelectorAll('a[href]');

  let buttonId = 0;
  anchorElements.forEach((anchor, index) => {
    const style = anchor.getAttribute('style') || '';

    // Check if this looks like a button (has background styling)
    if (!isButtonLink(style)) return;

    // Get button text from nested span or direct content
    const spanElement = anchor.querySelector('span');
    const buttonText = cleanTextContent(spanElement?.textContent || anchor.textContent || '');

    // Skip empty buttons
    if (!buttonText.trim()) return;

    // Skip if we've already seen this button text (responsive duplicates)
    if (seenText.has(buttonText)) return;
    seenText.add(buttonText);

    // Extract style information
    const backgroundColor = extractBackgroundColor(style);
    const textColor = spanElement
      ? extractStyleValue(spanElement.getAttribute('style') || '', 'color')
      : extractStyleValue(style, 'color');

    buttons.push({
      id: `button_${++buttonId}`,
      text: buttonText,
      originalHref: anchor.getAttribute('href') || '#',
      selector: `a[href]:nth-of-type(${index + 1})`,
      style: {
        backgroundColor: backgroundColor || undefined,
        textColor: textColor || undefined
      }
    });
  });

  return buttons;
}

/**
 * Check if a link is styled as a button
 */
function isButtonLink(style: string): boolean {
  const styleLower = style.toLowerCase();
  return (
    styleLower.includes('background') ||
    styleLower.includes('display:table') ||
    styleLower.includes('border-radius')
  );
}

/**
 * Extract a specific CSS property value from inline style string
 */
function extractStyleValue(style: string, property: string): string | null {
  const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i');
  const match = style.match(regex);
  return match ? match[1].trim() : null;
}

/**
 * Extract background color from style (handles linear-gradient too)
 */
function extractBackgroundColor(style: string): string | null {
  // Try background-color first
  let color = extractStyleValue(style, 'background-color');
  if (color) return color;

  // Try background (might be gradient or solid)
  const background = extractStyleValue(style, 'background');
  if (background) {
    // If it's a gradient, extract the first color
    const gradientMatch = background.match(/linear-gradient\([^,]+,\s*([^,)]+)/i);
    if (gradientMatch) {
      return gradientMatch[1].trim();
    }
    // If it's a solid color
    if (background.startsWith('#') || background.startsWith('rgb')) {
      return background;
    }
  }

  return null;
}

/**
 * Clean text content by removing extra whitespace and line breaks
 */
function cleanTextContent(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\u00a0/g, ' ') // non-breaking spaces
    .trim();
}

/**
 * Find and replace text content in HTML by selector-like matching
 *
 * Since we can't use actual CSS selectors reliably on the raw HTML string,
 * we use a content-based approach to find and replace text.
 */
export function replaceTextInHtml(
  html: string,
  originalContent: string,
  newContent: string
): string {
  // Escape special regex characters in the original content
  const escapedOriginal = originalContent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create a pattern that matches the text in a td element
  // Handle potential whitespace differences and <br> tags
  const flexiblePattern = escapedOriginal
    .split(/\s+/)
    .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\s*(?:<br\\s*/?>)?\\s*');

  const regex = new RegExp(
    `(<td[^>]*dir=["']ltr["'][^>]*>)([^<]*${flexiblePattern}[^<]*)(</td>)`,
    'gi'
  );

  return html.replace(regex, (match, openTag, _textContent, closeTag) => {
    return `${openTag}${newContent}${closeTag}`;
  });
}

/**
 * Find and replace button href in HTML
 */
export function replaceButtonHrefInHtml(
  html: string,
  originalHref: string,
  newHref: string
): string {
  // Simple href replacement
  const escapedHref = originalHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`href=["']${escapedHref}["']`, 'gi');
  return html.replace(regex, `href="${newHref}"`);
}

/**
 * Inject data attributes and interactive styles into HTML for the mapping dialog.
 * This pre-processes the HTML so the iframe can display highlights and handle clicks.
 */
export function injectInteractiveAttributes(
  html: string,
  textBlocks: TextBlock[],
  buttons: Button[]
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Track which content we've already marked (for deduplication)
  const markedTextContent = new Set<string>();
  const markedButtonText = new Set<string>();

  // Mark text blocks
  const tdElements = doc.querySelectorAll('td[dir="ltr"]');
  tdElements.forEach((td) => {
    const rawText = td.textContent || '';
    const cleanedText = cleanTextContent(rawText);

    if (!cleanedText.trim()) return;
    if (markedTextContent.has(cleanedText)) return;

    // Find matching text block
    const block = textBlocks.find(b => b.content === cleanedText);
    if (block) {
      td.setAttribute('data-canva-text-id', block.id);
      markedTextContent.add(cleanedText);
    }
  });

  // Mark buttons
  const anchorElements = doc.querySelectorAll('a[href]');
  anchorElements.forEach((anchor) => {
    const style = anchor.getAttribute('style') || '';
    if (!isButtonLinkCheck(style)) return;

    const spanElement = anchor.querySelector('span');
    const buttonText = cleanTextContent(spanElement?.textContent || anchor.textContent || '');

    if (!buttonText.trim()) return;
    if (markedButtonText.has(buttonText)) return;

    // Find matching button
    const button = buttons.find(b => b.text === buttonText);
    if (button) {
      anchor.setAttribute('data-canva-button-id', button.id);
      markedButtonText.add(buttonText);
    }
  });

  // Add interactive styles and script to head
  const styleEl = doc.createElement('style');
  styleEl.textContent = `
    [data-canva-text-id],
    [data-canva-button-id] {
      transition: outline 0.15s ease, background-color 0.15s ease;
    }
    [data-canva-text-id].canva-highlight {
      outline: 2px solid #3b82f6 !important;
      outline-offset: 2px !important;
      cursor: pointer !important;
      background-color: rgba(59, 130, 246, 0.1) !important;
    }
    [data-canva-button-id].canva-highlight {
      outline: 2px solid #3b82f6 !important;
      outline-offset: 2px !important;
      cursor: pointer !important;
    }
    [data-canva-text-id].canva-selected,
    [data-canva-button-id].canva-selected {
      outline: 3px solid #22c55e !important;
      outline-offset: 2px !important;
      background-color: rgba(34, 197, 94, 0.15) !important;
    }
    [data-canva-text-id]:hover.canva-highlight,
    [data-canva-button-id]:hover.canva-highlight {
      outline-width: 3px !important;
      background-color: rgba(59, 130, 246, 0.2) !important;
    }
    [data-canva-button-id].canva-button-hover {
      outline: 3px solid #3b82f6 !important;
      outline-offset: 4px !important;
      background-color: rgba(59, 130, 246, 0.15) !important;
    }
    [data-canva-text-id].canva-settings-hover,
    [data-canva-button-id].canva-settings-hover {
      outline: 2px dashed #f97316 !important;
      outline-offset: 2px !important;
      background-color: rgba(249, 115, 22, 0.15) !important;
    }
  `;
  doc.head.appendChild(styleEl);

  // Add script for click handling and communication with parent
  const scriptEl = doc.createElement('script');
  scriptEl.textContent = `
    (function() {
      // Listen for messages from parent to update highlights
      window.addEventListener('message', function(e) {
        if (e.data.type === 'updateHighlights') {
          // Clear all highlights
          document.querySelectorAll('.canva-highlight, .canva-selected, .canva-button-hover, .canva-settings-hover').forEach(function(el) {
            el.classList.remove('canva-highlight', 'canva-selected', 'canva-button-hover', 'canva-settings-hover');
          });

          // Add selected highlights for text (green)
          if (e.data.selectedTextIds) {
            e.data.selectedTextIds.forEach(function(id) {
              var el = document.querySelector('[data-canva-text-id="' + id + '"]');
              if (el) el.classList.add('canva-selected');
            });
          }

          // Add settings panel hover highlight (orange dashed) - works for both text and buttons
          if (e.data.hoveredElementId) {
            var hoveredEl = document.querySelector('[data-canva-text-id="' + e.data.hoveredElementId + '"]') ||
                           document.querySelector('[data-canva-button-id="' + e.data.hoveredElementId + '"]');
            if (hoveredEl) {
              // Remove selected class temporarily to show hover, then re-add after animation
              var wasSelected = hoveredEl.classList.contains('canva-selected');
              if (wasSelected) {
                hoveredEl.classList.remove('canva-selected');
              }
              hoveredEl.classList.add('canva-settings-hover');
            }
          }

          // Add selection mode highlights (blue)
          if (e.data.selectionMode === 'text') {
            document.querySelectorAll('[data-canva-text-id]').forEach(function(el) {
              if (!el.classList.contains('canva-selected') && !el.classList.contains('canva-settings-hover')) {
                el.classList.add('canva-highlight');
              }
            });
          } else if (e.data.selectionMode === 'button') {
            document.querySelectorAll('[data-canva-button-id]').forEach(function(el) {
              el.classList.add('canva-highlight');
            });
          }
        }
      });

      // Handle clicks
      document.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        var target = e.target;

        // Check for text block click
        var textEl = target.closest('[data-canva-text-id]');
        if (textEl) {
          window.parent.postMessage({
            type: 'canvaElementClick',
            elementType: 'text',
            elementId: textEl.getAttribute('data-canva-text-id')
          }, '*');
          return;
        }

        // Check for button click
        var buttonEl = target.closest('[data-canva-button-id]');
        if (buttonEl) {
          window.parent.postMessage({
            type: 'canvaElementClick',
            elementType: 'button',
            elementId: buttonEl.getAttribute('data-canva-button-id')
          }, '*');
          return;
        }
      });
    })();
  `;
  doc.body.appendChild(scriptEl);

  return doc.documentElement.outerHTML;
}

// Helper for isButtonLink check (same logic as isButtonLink but exported for use in inject)
function isButtonLinkCheck(style: string): boolean {
  const styleLower = style.toLowerCase();
  return (
    styleLower.includes('background') ||
    styleLower.includes('display:table') ||
    styleLower.includes('border-radius')
  );
}
